import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BriefDraftSchema, BriefDetailsSchema, BriefMediaSchema, InspirationUrlSchema, briefValidationProblem } from "@prototypical/types";
import { startProblem, validOptionalEmail, type FixStep } from "../shared/startProblem";
import { putUpload } from "../shared/upload";
import { api, type BriefAttachment, type BusinessType, type InspoPreview, type SubdomainCheck } from "../../../lib/api";
import {
  clearWizardDraft, clearWizardRequestId, getOrCreateWizardRequestId,
  loadWizardDraft, saveWizardDraft,
} from "../../../lib/wizardStorage";
import { inferImageMime, isImageFile } from "../../../lib/imageTypes";
import {
  BUSINESS_TYPES,
  CAPABILITIES,
  defaultHours,
  integrationsForType,
  type Hours,
} from "../../../lib/catalog";
import { suggestSubdomain, normalizeUrl } from "../shared";
import type { InspoSlot } from "./components/InspoPreviewCard";

export type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export const STEP_NAMES = [
  "About", "Basics", "Contact", "What it does",
  "Integrations", "Inspiration", "Anything else", "Review",
];

export function useScratchWizard() {

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState<Step>(1);

  // Step 1
  const [type, setType] = useState<BusinessType | "">("");

  // Step 2
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [description, setDescription] = useState("");
  const onboardingIntentHandled = useRef(false);
  const [onboardingError, setOnboardingError] = useState<string | null>(null);

  // An opaque intent ID can appear in the URL after SSO, but the raw prompt
  // never does. Consume it once with the authenticated product token, then
  // immediately remove the identifier from history.
  useEffect(() => {
    const intentId = searchParams.get("onboarding");
    if (!intentId || onboardingIntentHandled.current) return;
    if (!/^[0-9a-f-]{36}$/i.test(intentId)) {
      setSearchParams({}, { replace: true });
      return;
    }
    onboardingIntentHandled.current = true;
    api.consumeOnboardingIntent(intentId)
      .then(({ prompt }) => {
        setDescription(prompt);
        setSearchParams({}, { replace: true });
      })
      .catch(() => {
        setOnboardingError("We couldn’t restore your website idea. You can still describe it below.");
        setSearchParams({}, { replace: true });
      });
  }, [searchParams, setSearchParams]);

  // Step 3
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [hours, setHours] = useState<Hours>(defaultHours());

  // Step 4 — capabilities multi-select
  const [capIds, setCapIds] = useState<string[]>(
    CAPABILITIES.filter((c) => c.alwaysOn).map((c) => c.id),
  );

  // Step 5 — integrations
  const [integrationIds, setIntegrationIds] = useState<string[]>([]);

  // Step 6 — motion permission and optional references. No aesthetic presets.
  const [motion, setMotion] = useState<boolean>(true);

  // Inspiration URLs + per-URL fetched preview state.
  // Dynamic list — start with one empty row, "+ Add another" appends.
  type InspoSlot =
    | { state: "idle" }
    | { state: "loading" }
    | { state: "ok"; preview: InspoPreview }
    | { state: "error"; message: string };
  const [inspoUrls, setInspoUrls] = useState<string[]>([""]);
  const [inspoPreviews, setInspoPreviews] = useState<InspoSlot[]>([{ state: "idle" }]);

  function addInspoRow() {
    setInspoUrls((u) => (u.length >= 8 ? u : [...u, ""]));
    setInspoPreviews((p) => (p.length >= 8 ? p : [...p, { state: "idle" }]));
  }

  function removeInspoRow(idx: number) {
    setInspoUrls((u) => {
      const next = u.filter((_, i) => i !== idx);
      return next.length === 0 ? [""] : next;
    });
    setInspoPreviews((p) => {
      const next = p.filter((_, i) => i !== idx);
      return next.length === 0 ? [{ state: "idle" } as InspoSlot] : next;
    });
  }

  // Step 7 — anything else + attachments (logo, photos, menus)
  const [ownerNote, setOwnerNote] = useState("");
  const [attachments, setAttachments] = useState<BriefAttachment[]>([]);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});
  const uploadErr = Object.entries(uploadErrors).map(([name, message]) => `${name}: ${message}`).join("\n") || null;
  const [zipSkipped, setZipSkipped] = useState<Array<{ name: string; reason: string }>>([]);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const photosInputRef = useRef<HTMLInputElement | null>(null);
  const zipInputRef = useRef<HTMLInputElement | null>(null);

  function recordUploadError(name: string, message: string) {
    setUploadErrors(previous => ({ ...previous, [name]: message }));
  }

  function clearUploadError(name: string) {
    setUploadErrors(previous => Object.fromEntries(Object.entries(previous).filter(([file]) => file !== name)));
  }

  async function uploadFile(file: File, tag: "logo" | "photo" | "doc"): Promise<BriefAttachment | null> {
    setUploading(file.name);
    setUploadProgress(0);
    try {
      // The browser's type is a guess (empty for HEIC on Windows Chrome); the
      // extension fills in, and the server's answer is what the brief records.
      const mimeType = inferImageMime(file);
      const sign = await api.signUpload({
        name: file.name,
        mimeType,
        sizeBytes: file.size,
        tag,
      });
      await putUpload(sign.signedPutUrl, sign.requiredHeaders, file, setUploadProgress);
      clearUploadError(file.name);
      return {
        name: file.name,
        mimeType: sign.mimeType || mimeType,
        sizeBytes: file.size,
        gcsPath: sign.gcsPath,
        tag,
        source: "owner-upload",
      };
    } catch (err: any) {
      recordUploadError(file.name, err?.payload?.message || err?.message || "The file could not be uploaded. Select it again to retry.");
      return null;
    } finally {
      setUploading(null);
      setUploadProgress(null);
    }
  }

  async function handleLogoPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isImageFile(file)) {
      recordUploadError(file.name, "Logo must be an image.");
      return;
    }
    const att = await uploadFile(file, "logo");
    if (att) {
      // Replace only after the new bytes have been saved successfully.
      setAttachments((cur) => [...cur.filter(a => a.tag !== "logo"), att]);
      const reader = new FileReader();
      reader.onload = () => setLogoPreview(typeof reader.result === "string" ? reader.result : null);
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  }

  async function handlePhotosPick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    for (const file of files) {
      if (!isImageFile(file)) {
        recordUploadError(file.name, "Choose an image, or put documents in a ZIP.");
        continue;
      }
      const att = await uploadFile(file, "photo");
      if (att) setAttachments((cur) => [...cur, att]);
    }
  }

  async function handleZipPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const isZip =
      file.type === "application/zip" ||
      file.type === "application/x-zip-compressed" ||
      file.type === "application/x-zip" ||
      /\.zip$/i.test(file.name);
    if (!isZip) {
      recordUploadError(file.name, "Please choose a .zip file.");
      return;
    }
    setZipSkipped([]);
    setUploadProgress(0);
    setUploading(file.name);
    try {
      // Sign with the browser's actual perceived MIME (or app/zip fallback)
      // so the Content-Type the browser sends matches what we signed.
      const browserMime =
        file.type && /zip/i.test(file.type) ? file.type : "application/zip";
      const sign = await api.signUpload({
        name: file.name,
        mimeType: browserMime,
        sizeBytes: file.size,
        tag: "zip",
      });
      await putUpload(sign.signedPutUrl, sign.requiredHeaders, file, setUploadProgress);
      setUploadProgress(null);
      setUploading(`Extracting ${file.name}…`);
      const { attachments: extracted, skipped } = await api.extractZip(sign.gcsPath);
      setAttachments((cur) => {
        const existing = new Set(cur.map((a) => a.gcsPath).filter(Boolean) as string[]);
        return [...cur, ...extracted.filter((a) => !a.gcsPath || !existing.has(a.gcsPath))];
      });
      const newLogo = extracted.find((a) => a.tag === "logo");
      if (newLogo && !logoPreview && newLogo.gcsPath) {
        setLogoPreview(gcsToPublicUrl(newLogo.gcsPath));
      }
      setZipSkipped(skipped);
      clearUploadError(file.name);
    } catch (err: any) {
      recordUploadError(file.name, err?.payload?.message || err?.message || "The ZIP could not be uploaded. Select it again to retry.");
    } finally {
      setUploading(null);
      setUploadProgress(null);
    }
  }

  function gcsToPublicUrl(gcsPath: string): string {
    const m = /^gs:\/\/([^/]+)\/(.+)$/.exec(gcsPath);
    if (!m) return "";
    return `https://storage.googleapis.com/${m[1]}/${m[2]}`;
  }

  function removeAttachment(gcsPath?: string) {
    setAttachments((cur) => cur.filter((a) => a.gcsPath !== gcsPath));
    setLogoPreview((p) => (p && attachments.find((a) => a.tag === "logo")?.gcsPath === gcsPath ? null : p));
  }

  // Step 8 — subdomain + submit
  const [subdomain, setSubdomain] = useState("");
  const [subdomainTouched, setSubdomainTouched] = useState(false);
  const [subCheck, setSubCheck] = useState<SubdomainCheck | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [submitFixStep, setSubmitFixStep] = useState<FixStep | undefined>();
  const [pendingBriefId, setPendingBriefId] = useState<string | null>(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const requestId = useRef(getOrCreateWizardRequestId("scratch"));

  useEffect(() => {
    const draft = loadWizardDraft<any>("scratch");
    if (draft) {
      if (draft.step >= 1 && draft.step <= 8) setStep(draft.step);
      if (BUSINESS_TYPES.some((item) => item.value === draft.type)) setType(draft.type);
      setName(draft.name ?? ""); setTagline(draft.tagline ?? ""); setDescription(draft.description ?? "");
      setAddress(draft.address ?? ""); setPhone(draft.phone ?? ""); setEmail(draft.email ?? "");
      if (draft.hours && typeof draft.hours === "object") setHours(draft.hours);
      if (Array.isArray(draft.capIds)) setCapIds(draft.capIds);
      if (Array.isArray(draft.integrationIds)) setIntegrationIds(draft.integrationIds);
      if (typeof draft.motion === "boolean") setMotion(draft.motion);
      if (Array.isArray(draft.inspoUrls) && draft.inspoUrls.length) {
        setInspoUrls(draft.inspoUrls.slice(0, 8));
        setInspoPreviews(draft.inspoUrls.slice(0, 8).map(() => ({ state: "idle" })));
      }
      setOwnerNote(draft.ownerNote ?? "");
      if (Array.isArray(draft.attachments)) setAttachments(draft.attachments);
      if (draft.uploadErrors && typeof draft.uploadErrors === "object") {
        const errors = Object.fromEntries(Object.entries(draft.uploadErrors).filter((entry): entry is [string, string] => typeof entry[1] === "string"));
        setUploadErrors(errors);
        if (Object.keys(errors).length && draft.step === 8) setStep(7);
      }
      setSubdomain(draft.subdomain ?? "");
      setSubdomainTouched(!!draft.subdomainTouched);
      setPendingBriefId(typeof draft.pendingBriefId === "string" ? draft.pendingBriefId : null);
    }
    setDraftLoaded(true);
  }, []);

  useEffect(() => {
    if (!draftLoaded || submitting) return;
    saveWizardDraft("scratch", {
      step, type, name, tagline, description, address, phone, email, hours,
      capIds, integrationIds, motion, inspoUrls, ownerNote, attachments,
      subdomain, subdomainTouched, pendingBriefId, uploadErrors,
    });
  }, [
    draftLoaded, submitting, step, type, name, tagline, description, address,
    phone, email, hours, capIds, integrationIds, motion, inspoUrls,
    ownerNote, attachments, subdomain, subdomainTouched, pendingBriefId, uploadErrors,
  ]);

  // Auto-suggest subdomain from name unless the user has touched it.
  useEffect(() => {
    if (!subdomainTouched && name) setSubdomain(suggestSubdomain(name));
  }, [name, subdomainTouched]);

  // Live subdomain check on step 8.
  useEffect(() => {
    if (step !== 8) return;
    const sub = subdomain.trim();
    if (!sub) { setSubCheck(null); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.checkSubdomain(sub)
        .then((r) => { if (!cancelled) setSubCheck(r); })
        .catch(() => { if (!cancelled) setSubCheck(null); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [subdomain, step]);

  const canStep1 = !!type;
  const canStep2 = BriefDetailsSchema.safeParse({ name, description }).success;
  const canStep3 = validOptionalEmail(email);
  const canStep4 = capIds.length > 0;
  const canStep5 = true;
  const canStep6 = inspoUrls.every(url => !url.trim() || InspirationUrlSchema.safeParse(url).success);
  const canStep7 = !uploadErr && BriefMediaSchema.safeParse({ attachments, ownerNote }).success;
  const canSubmit = !!subCheck?.available && !submitting && !uploading && !uploadErr;

  function toggleCap(id: string) {
    const def = CAPABILITIES.find((c) => c.id === id);
    if (def?.alwaysOn) return;
    setCapIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  function toggleIntegration(id: string) {
    setIntegrationIds((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    );
  }

  function setInspo(idx: number, val: string) {
    setInspoUrls((cur) => cur.map((u, i) => (i === idx ? val : u)));
    setInspoPreviews((cur) => cur.map((p, i) => (i === idx ? { state: "idle" } : p)));
  }

  // Debounced preview fetch per URL slot. Only fires when the current
  // value parses as a real http(s) URL — anything shorter just stays
  // idle so the user isn't pinging /inspo-preview for every keystroke.
  useEffect(() => {
    if (step !== 6) return;
    const timers = inspoUrls.map((raw, idx) => {
      const url = normalizeUrl(raw);
      if (!url) return null;
      try {
        const u = new URL(url);
        if (!/^https?:$/.test(u.protocol)) return null;
        if (u.hostname.length < 4 || !u.hostname.includes(".")) return null;
      } catch {
        return null;
      }
      let cancelled = false;
      const t = window.setTimeout(() => {
        setInspoPreviews((cur) => cur.map((p, i) => (i === idx ? { state: "loading" } : p)));
        api.getInspoPreview(url)
          .then((preview) => {
            if (cancelled) return;
            setInspoPreviews((cur) => cur.map((p, i) => (i === idx ? { state: "ok", preview } : p)));
          })
          .catch((err: any) => {
            if (cancelled) return;
            const msg = err?.payload?.error || "Couldn't load preview";
            setInspoPreviews((cur) => cur.map((p, i) => (i === idx ? { state: "error", message: msg } : p)));
          });
      }, 600);
      return () => { cancelled = true; window.clearTimeout(t); };
    });
    return () => { for (const cancel of timers) cancel?.(); };
  }, [inspoUrls, step]);

  function setHour(day: string, patch: Partial<Hours[string]>) {
    setHours((cur) => ({ ...cur, [day]: { ...cur[day], ...patch } }));
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitErr(null);
    setSubmitFixStep(undefined);
    const inspo = inspoUrls.filter(url => url.trim()).map(url => ({ url, weight: 1 }));
    const parsed = BriefDraftSchema.safeParse({
        source: "questionnaire",
        business: {
          name: name.trim(),
          type: type as BusinessType,
          tagline: tagline.trim() || undefined,
          description: description.trim(),
          contact: (address || phone || email) ? {
            address: address.trim() || undefined,
            phone: phone.trim() || undefined,
            email: email.trim() || undefined,
          } : undefined,
          hours,
        },
        capabilities: capIds,
        integrations: integrationIds.length ? integrationIds : undefined,
        inspo,
        attachments: attachments.length ? attachments : undefined,
        ownerNote: ownerNote.trim() || undefined,
        voice: { motion },
    });
    if (!parsed.success) {
      const problem = startProblem({ payload: briefValidationProblem(parsed.error.issues) });
      setSubmitErr(problem.message);
      setSubmitFixStep(problem.step);
      return;
    }
    setSubmitting(true);
    try {
      const briefId = pendingBriefId ?? (await api.createBrief(parsed.data)).brief.id;
      if (!pendingBriefId) setPendingBriefId(briefId);
      const siteRes = await api.startNewSite({
        subdomain: subdomain.trim().toLowerCase(),
        briefId,
        kickBuild: true,
        requestId: requestId.current,
      });
      clearWizardDraft("scratch");
      clearWizardRequestId("scratch");
      if (siteRes.jobId) {
        navigate(`/sites/${siteRes.site.id}/builds/${siteRes.jobId}`);
      } else {
        navigate(`/sites/${siteRes.site.id}`);
      }
    } catch (err: any) {
      const problem = startProblem(err);
      setSubmitErr(problem.message);
      setSubmitFixStep(problem.step);
      setSubmitting(false);
    }
  }

  const eligibleIntegrations = type ? integrationsForType(type as BusinessType) : [];

  return {
    step, setStep, type, setType, name, setName, tagline, setTagline, description, setDescription,
    onboardingError, address, setAddress, phone, setPhone, email, setEmail, hours, setHour,
    capIds, toggleCap, integrationIds, toggleIntegration, motion, setMotion,
    inspoUrls, inspoPreviews, setInspo, addInspoRow, removeInspoRow,
    ownerNote, setOwnerNote, attachments, logoPreview, setLogoPreview,
    uploading, uploadProgress, uploadErr, dismissUploadError: () => setUploadErrors({}), zipSkipped,
    logoInputRef, photosInputRef, zipInputRef,
    handleLogoPick, handlePhotosPick, handleZipPick, removeAttachment,
    subdomain, setSubdomain, setSubdomainTouched, subCheck,
    submitting, submitErr, submitFixStep, canSubmit, handleSubmit,
    canStep1, canStep2, canStep3, canStep4, canStep5, canStep6, canStep7,
    eligibleIntegrations,
  };
}
